import React, { useEffect, useCallback, useContext, useMemo } from "react";
import { InputSelect } from "@bookingcom/bui-react";
import { format as formatDate } from "date-fns";
import useCalendarSelect from "./useCalendarSelect.js";
import { t } from "@bookingcom/lingojs-core";
import { useFareCalendarEnabled, useFareCalendar } from "../FareCalendar/useFareCalendar.js";
import { useSearchboxStore } from "../../store/searchBoxStore.js";
import { SearchBoxContext } from "../SearchBoxContext/index.js";
import FareCalendarBadge from "../FareCalendar/FareCalendarBadge.js";
import styles from "./CalendarSelect.module.css";
export default function CalendarSelect(props) {
    const { segmentIndex, onDateChange, variant = "vertical" } = props;
    const store = useSearchboxStore();
    const { fetchedRanges, data: fareCalendarData } = store.searchbox.fareCalendar;
    const { isMobile, fareCalendarConfig } = useContext(SearchBoxContext);
    const mode = isMobile ? "vertical" : "desktop";
    const fareCalendarEnabled = useFareCalendarEnabled(segmentIndex, fareCalendarConfig, mode);
    const { dateValues, fromDay, fromYearMonth, fromYearMonthList, getDaysList, i18n, isRoundTrip, onFromDayChange, onFromYearMonthChange, onToDayChange, onToYearMonthChange, toDay, toYearMonth, toYearMonthList, startDate } = useCalendarSelect({ segmentIndex });
    const singleMonth = useMemo(() => {
        return isRoundTrip && fromYearMonth === toYearMonth;
    }, [isRoundTrip, fromYearMonth, toYearMonth]);
    const { handleMonthChange } = useFareCalendar({
        baseDate: startDate || undefined,
        mode: isMobile ? "vertical" : "desktop",
        enabled: fareCalendarEnabled,
        segmentIndex,
        singleMonth,
        disableAutoFetch: true,
        selectedDepartureDate: dateValues.from,
        selectedReturnDate: dateValues.to
    });
    const enhanceDaysListWithPrice = useCallback((daysList, yearMonth) => {
        if (!fareCalendarEnabled || !yearMonth)
            return daysList;
        return daysList.map((dayOption) => {
            if (!dayOption.value || dayOption.disabled)
                return dayOption;
            const [year, month] = yearMonth.split("-").map(Number);
            const day = Number(dayOption.value);
            const date = new Date(year, month, day);
            const dateKey = formatDate(date, "yyyy-MM-dd");
            const dateInFetchedRange = fetchedRanges.some((range) => {
                const rangeStart = new Date(range.start);
                const rangeEnd = new Date(range.end);
                return date >= rangeStart && date <= rangeEnd;
            });
            if (dateInFetchedRange) {
                const priceData = fareCalendarData[dateKey];
                if (priceData?.formattedPrice) {
                    return {
                        ...dayOption,
                        text: `${dayOption.text} ${priceData.formattedPrice}`
                    };
                }
                return {
                    ...dayOption,
                    text: `${dayOption.text} -`
                };
            }
            return dayOption;
        });
    }, [fareCalendarEnabled, fareCalendarData, fetchedRanges]);
    useEffect(() => {
        onDateChange(dateValues);
    }, [dateValues, onDateChange]);
    useEffect(() => {
        if (!fromYearMonth)
            return;
        if (isRoundTrip && dateValues.from) {
            return;
        }
        const [year, month] = fromYearMonth.split("-").map(Number);
        const monthDate = new Date(year, month, 1);
        handleMonthChange(monthDate);
    }, [fromYearMonth, isRoundTrip, dateValues.from, handleMonthChange]);
    useEffect(() => {
        if (!isRoundTrip || !toYearMonth || !dateValues.from)
            return;
        const [year, month] = toYearMonth.split("-").map(Number);
        const monthDate = new Date(year, month, 1);
        handleMonthChange(monthDate);
    }, [toYearMonth, isRoundTrip, dateValues.from, handleMonthChange]);
    const defaultOption = { text: "-", value: "", disabled: true };
    return (React.createElement(React.Fragment, null,
        fareCalendarEnabled && fareCalendarConfig?.currencyCode ? (React.createElement(FareCalendarBadge, { currencyCode: fareCalendarConfig.currencyCode, className: styles.fareCalendarBadge })) : undefined,
        React.createElement("div", { className: `${styles.wrapper} ${variant === "horizontal" ? styles.wrapperHorizontal : ""}` },
            React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_departure_month")), name: `searchbox-date-from-month-year-${segmentIndex}`, options: [defaultOption, ...fromYearMonthList], value: fromYearMonth, onChange: ({ value }) => {
                    onFromYearMonthChange(value);
                } }),
            React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_departure_date")), name: `searchbox-date-from-day-${segmentIndex}`, options: [defaultOption, ...enhanceDaysListWithPrice(getDaysList(fromYearMonth), fromYearMonth)], disabled: !fromYearMonth, value: fromDay, onChange: ({ value }) => {
                    onFromDayChange(value);
                } }),
            isRoundTrip ? (React.createElement(React.Fragment, null,
                React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_return_month")), name: `searchbox-date-to-month-year-${segmentIndex}`, options: [defaultOption, ...toYearMonthList], value: toYearMonth, disabled: !fromDay, onChange: ({ value }) => {
                        onToYearMonthChange(value);
                    } }),
                React.createElement(InputSelect, { label: i18n.trans(t("flights_index_search_box_return_date")), name: `searchbox-date-to-day-${segmentIndex}`, options: [
                        defaultOption,
                        ...enhanceDaysListWithPrice(getDaysList(toYearMonth, dateValues.from), toYearMonth)
                    ], disabled: !toYearMonth, value: toDay, onChange: ({ value }) => {
                        onToDayChange(value);
                    } }))) : null)));
}
